// E2EBridge.tsx — Mounted inside the <Canvas> only under ?e2e=1. Publishes the
// camera-dependent half of the playtest control surface (see ../e2e.ts): a
// world→screen projector so the harness can drive a real mouse hover over a
// star, and the live three.js renderer stats for the perf report. Renders
// nothing.
import { useEffect } from 'react';
import { useThree } from '@react-three/fiber';
import { Vector3 } from 'three';
import { e2eAssign, e2eBodyNode } from '../e2e';
import { gameClock } from '../state/gameClock';

export function E2EBridge() {
  const camera = useThree((s) => s.camera);
  const size = useThree((s) => s.size);
  const gl = useThree((s) => s.gl);

  useEffect(() => {
    const v = new Vector3();
    const toScreen = (x: number, y: number, z: number) => {
      v.set(x, y, z).project(camera);
      // z>1 means the point is behind the camera / clipped.
      if (v.z > 1) {
        return null;
      }
      return {
        x: (v.x * 0.5 + 0.5) * size.width,
        y: (-v.y * 0.5 + 0.5) * size.height,
      };
    };
    e2eAssign({
      screenOf: (worldPos) => toScreen(worldPos[0], worldPos[1], worldPos[2]),
      screenOfPlanet: (id) => {
        const node = e2eBodyNode(id);
        if (!node) {
          return null;
        }
        node.getWorldPosition(v);
        return toScreen(v.x, v.y, v.z);
      },
      setClockSpeed: (speed) => {
        gameClock.speed = speed;
      },
      renderInfo: () => ({
        calls: gl.info.render.calls,
        triangles: gl.info.render.triangles,
        geometries: gl.info.memory.geometries,
        textures: gl.info.memory.textures,
        programs: gl.info.programs?.length ?? 0,
      }),
      // Pin the simulation clock so orbital bodies hold a fixed phase, making
      // screenshots reproducible run-to-run.
      freezeClock: (time = 0) => {
        gameClock.time = time;
        gameClock.speed = 0;
      },
      canvasReady: true,
    });
  }, [camera, size.width, size.height, gl]);

  return null;
}
